import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { Zap, LayoutDashboard, Package, Users, Bell, LogOut, BarChart3, UserCircle, GitBranch, Calendar, BookOpen, CheckSquare, Forklift, Menu, X } from "lucide-react";
import QuickActionWidget from "@/components/QuickActionWidget";

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Auto-close on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Block body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  // Esc closes
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setMobileOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const navItems = [
    { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true },
    { to: "/admin/inventory", icon: Package, label: "Inventory" },
    { to: "/admin/requests", icon: Users, label: "Requests" },
    { to: "/admin/notifications", icon: Bell, label: "Notifications" },
    { to: "/admin/tasks", icon: CheckSquare, label: "My Tasks" },
    { to: "/admin/automation", icon: Zap, label: "Automation" },
  ];

  const crmItems = [
    { to: "/admin/crm", icon: BarChart3, label: "CRM Dashboard", end: true },
    { to: "/admin/crm/clients", icon: UserCircle, label: "Clients" },
    { to: "/admin/crm/pipeline", icon: GitBranch, label: "Pipeline" },
    { to: "/admin/crm/appointments", icon: Calendar, label: "Appointments" },
    { to: "/admin/crm/guide", icon: BookOpen, label: "Guide & Tips" },
  ];

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-sm transition-colors ${
      isActive
        ? "bg-yellow-50 text-gray-900 border-l-2 border-yellow-400"
        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
    }`;

  return (
    <div data-testid="admin-layout" className="min-h-screen bg-gray-50 flex">
      {/* Mobile hamburger button (visible only <lg) */}
      <button
        data-testid="admin-mobile-menu-toggle"
        onClick={() => setMobileOpen(true)}
        aria-label="Open menu"
        className="lg:hidden fixed top-3 left-3 z-30 w-10 h-10 bg-white border border-gray-200 rounded-sm shadow-sm flex items-center justify-center text-gray-700 hover:bg-gray-50"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Backdrop on mobile */}
      {mobileOpen && (
        <div
          data-testid="admin-mobile-backdrop"
          onClick={() => setMobileOpen(false)}
          className="lg:hidden fixed inset-0 bg-black/50 z-40 transition-opacity"
        />
      )}

      {/* Sidebar */}
      <aside
        data-testid="admin-sidebar"
        className={`bg-white border-r border-gray-200 flex flex-col z-50 transition-transform duration-200 ease-out
          fixed inset-y-0 left-0 w-64 transform ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
          lg:translate-x-0 lg:static lg:w-60`}
      >
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-yellow-400 rounded-sm flex items-center justify-center">
              <Forklift className="w-4 h-4 text-gray-900" />
            </div>
            <div>
              <p className="font-heading text-sm font-bold text-gray-900">Fork Finder</p>
              <p className="text-[10px] text-gray-400 tracking-wide">by Gesti&oacute;n Farcar</p>
            </div>
          </div>
          {/* Close button on mobile */}
          <button
            data-testid="admin-mobile-menu-close"
            onClick={() => setMobileOpen(false)}
            aria-label="Close menu"
            className="lg:hidden w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-900"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-[10px] text-gray-400 px-4">Admin Panel</p>

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`admin-nav-${item.label.toLowerCase()}`}
              className={linkClass}
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </NavLink>
          ))}

          <div className="pt-3 mt-3 border-t border-gray-200">
            <p className="text-[10px] tracking-[0.15em] uppercase font-bold text-gray-400 px-3 mb-2">CRM</p>
            {crmItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={linkClass}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </NavLink>
            ))}
          </div>
        </nav>

        <div className="p-3 border-t border-gray-200">
          <p className="text-xs text-gray-500 mb-2 px-3 truncate">{user?.email}</p>
          <button
            data-testid="admin-logout-btn"
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-sm w-full"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </aside>

      {/* Main content — extra top padding on mobile so hamburger doesn't overlap content */}
      <main className="flex-1 overflow-auto pt-14 lg:pt-0">
        <Outlet />
      </main>

      <QuickActionWidget />
    </div>
  );
}
